/**
 * Auth Module — full-screen gate, Supabase Auth.
 * Supports: email/password, magic link, Google OAuth, password reset (recovery),
 * multi-user shared-device isolation, and safe sign-out.
 *
 * Exposes window.Auth = { initAuth, showAuthScreen, hideAuthScreen, signOut, mapAuthError, purgeAllLocalDatabases }
 */
let _exported;

(function () {

    // ── State ─────────────────────────────────────────────────────────────────
    let _activeTab   = 'email';  // 'email' | 'magic' | 'google'
    let _emailMode   = 'signin'; // 'signin' | 'signup'
    let _activePane  = 'email';  // 'email' | 'magic' | 'google' | 'forgot' | 'reset'
    let _isRecovery  = false;
    let _initialized = false;
    let _bootTimer   = null;
    const PENDING_IDB_PURGE_KEY = 'pending_idb_purge';

    // ── DOM helpers ───────────────────────────────────────────────────────────

    function $(id) { return document.getElementById(id); }

    function showAuthScreen() {
        const screen = $('auth-screen');
        if (screen) screen.style.display = 'flex';
        document.body.classList.add('auth-gated');
    }

    function hideAuthScreen() {
        const screen = $('auth-screen');
        if (screen) screen.style.display = 'none';
        document.body.classList.remove('auth-gated');
    }

    function setError(msg) {
        const el = $('auth-error');
        if (!el) return;
        el.textContent = msg || '';
        el.style.display = msg ? 'block' : 'none';
    }

    function mapAuthError(err) {
        if (!err) return '';
        const msg = typeof err === 'string' ? err : err.message || '';
        if (/failed to fetch/i.test(msg) || /network/i.test(msg)) {
            return 'Unable to reach the server. Please check your internet connection.';
        }
        if (/invalid login credentials/i.test(msg)) {
            return 'Incorrect email or password. Please try again.';
        }
        if (/email not confirmed/i.test(msg)) {
            return 'Please check your email to confirm your account before signing in.';
        }
        if (/user already registered/i.test(msg)) {
            return 'An account with this email already exists. Please sign in instead.';
        }
        if (/password should be at least/i.test(msg)) {
            return 'Password must be at least 6 characters long.';
        }
        return msg;
    }

    function setLoading(loading) {
        const submitBtn  = $('auth-submit-btn');
        const submitText = $('auth-submit-text');
        const magicBtn   = $('auth-magic-btn');
        const magicText  = $('auth-magic-text');
        const forgotBtn  = $('auth-forgot-btn');
        const forgotText = $('auth-forgot-text');
        const resetBtn   = $('auth-reset-btn');
        const resetText  = $('auth-reset-text');
        const googleBtn  = $('auth-google-btn');

        if (submitBtn) {
            submitBtn.disabled = loading;
            if (submitText) submitText.textContent = loading ? 'Please wait…' : (_emailMode === 'signin' ? 'Sign In' : 'Create Account');
        }
        if (magicBtn) {
            magicBtn.disabled = loading;
            if (magicText) magicText.textContent = loading ? 'Sending…' : 'Send Magic Link';
        }
        if (forgotBtn) {
            forgotBtn.disabled = loading;
            if (forgotText) forgotText.textContent = loading ? 'Sending…' : 'Send Reset Link';
        }
        if (resetBtn) {
            resetBtn.disabled = loading;
            if (resetText) resetText.textContent = loading ? 'Saving…' : 'Save New Password';
        }
        if (googleBtn) {
            googleBtn.disabled = loading;
        }
    }

    // ── Tab & Pane switching ──────────────────────────────────────────────────

    function switchTab(tab) {
        _activeTab = tab;
        _activePane = tab;
        setError('');

        const tabsBar = $('auth-tabs-bar');
        if (tabsBar) tabsBar.style.display = 'flex';

        ['email', 'magic', 'google', 'forgot', 'reset'].forEach(t => {
            const btn  = $('auth-tab-' + t);
            const pane = $('auth-pane-' + t);
            if (btn) {
                btn.classList.toggle('active', t === tab);
                btn.setAttribute('aria-selected', String(t === tab));
            }
            if (pane) {
                pane.style.display = (t === tab) ? 'flex' : 'none';
                pane.hidden = t !== tab;
            }
        });
    }

    function switchPane(paneName) {
        _activePane = paneName;
        setError('');

        const tabsBar = $('auth-tabs-bar');
        if (tabsBar) tabsBar.style.display = (paneName === 'forgot' || paneName === 'reset') ? 'none' : 'flex';

        ['email', 'magic', 'google', 'forgot', 'reset'].forEach(t => {
            const pane = $('auth-pane-' + t);
            if (pane) {
                pane.style.display = (t === paneName) ? 'flex' : 'none';
                pane.hidden = t !== paneName;
            }
        });
    }

    function switchEmailMode(mode) {
        _emailMode = mode;
        setError('');

        const submitText = $('auth-submit-text');
        const toggleEl   = $('auth-mode-toggle');
        const titleEl    = $('auth-email-title');
        const confirmGrp = $('auth-signup-confirm-group');
        const termsGrp   = $('auth-terms-group');
        const forgotRow  = $('auth-forgot-row');

        if (submitText) submitText.textContent = mode === 'signin' ? 'Sign In' : 'Create Account';
        if (titleEl)    titleEl.textContent    = mode === 'signin' ? 'Sign in to StudyMeneer' : 'Create your account';
        if (confirmGrp) confirmGrp.style.display = mode === 'signin' ? 'none' : 'block';
        if (termsGrp)   termsGrp.style.display   = mode === 'signin' ? 'none' : 'block';
        if (forgotRow)  forgotRow.style.display  = mode === 'signin' ? 'flex' : 'none';

        if (toggleEl) {
            toggleEl.innerHTML = mode === 'signin'
                ? 'No account? <a href="#" id="auth-switch-link">Sign up</a>'
                : 'Already have an account? <a href="#" id="auth-switch-link">Sign in</a>';
            const link = $('auth-switch-link');
            if (link) link.addEventListener('click', (e) => { e.preventDefault(); switchEmailMode(mode === 'signin' ? 'signup' : 'signin'); });
        }
    }

    // ── Auth flows ────────────────────────────────────────────────────────────

    async function handleEmailSubmit(e) {
        e.preventDefault();
        const email = $('auth-email-input').value.trim();
        const password = $('auth-password-input').value;
        if (!email || !password) { setError('Please enter your email and password.'); return; }

        if (_emailMode === 'signup') {
            if (password.length < 6) {
                setError('Password must be at least 6 characters long.');
                return;
            }
            const confirmInput = $('auth-password-confirm');
            if (!confirmInput || confirmInput.value !== password) {
                setError('Passwords do not match. Please verify your password.');
                return;
            }
            const termsCheckbox = $('auth-terms-checkbox');
            if (!termsCheckbox || !termsCheckbox.checked) {
                setError('Please agree to the Terms of Service and Privacy Policy to continue.');
                return;
            }
        }

        setLoading(true);
        setError('');

        const client = window.SupabaseClient;
        let result;

        if (_emailMode === 'signin') {
            result = await client.auth.signInWithPassword({ email, password });
        } else {
            result = await client.auth.signUp({ email, password });
        }

        setLoading(false);

        if (result.error) {
            setError(mapAuthError(result.error));
            return;
        }

        if (_emailMode === 'signup' && result.data.user && !result.data.session) {
            setError('');
            showConfirmationMessage(email);
        }
        // Session established → onAuthStateChange will fire and call initializeApp()
    }

    async function handleForgotPasswordSubmit(e) {
        e.preventDefault();
        const email = $('auth-forgot-email').value.trim();
        if (!email) { setError('Please enter your email address.'); return; }

        setLoading(true);
        setError('');

        const { error } = await window.SupabaseClient.auth.resetPasswordForEmail(email, {
            redirectTo: window.location.origin
        });

        setLoading(false);

        if (error) {
            setError(mapAuthError(error));
            return;
        }

        const pane = $('auth-pane-forgot');
        if (pane) pane.innerHTML = `
            <div class="auth-success-msg">
                <div class="auth-success-icon">✉</div>
                <div class="auth-success-title">Check your email</div>
                <div class="auth-success-body">We sent a password reset link to <strong>${email}</strong>. Click it to set a new password.</div>
                <button class="auth-link-btn" id="auth-back-from-forgot-success">Back to sign in</button>
            </div>`;
        const back = $('auth-back-from-forgot-success');
        if (back) back.addEventListener('click', () => { switchTab('email'); switchEmailMode('signin'); });
    }

    async function handleResetPasswordSubmit(e) {
        e.preventDefault();
        const password = $('auth-reset-password').value;
        const confirm = $('auth-reset-confirm').value;

        if (!password || password.length < 6) {
            setError('Password must be at least 6 characters long.');
            return;
        }
        if (password !== confirm) {
            setError('Passwords do not match. Please verify your new password.');
            return;
        }

        setLoading(true);
        setError('');

        const { data, error } = await window.SupabaseClient.auth.updateUser({ password });

        setLoading(false);

        if (error) {
            setError(mapAuthError(error));
            return;
        }

        // Successfully updated password. Clean recovery state from URL.
        try {
            history.replaceState(null, '', window.location.pathname + window.location.search);
        } catch (_) {}

        _isRecovery = false;

        if (data?.user) {
            onSessionReady(data.user);
        } else {
            switchTab('email');
            switchEmailMode('signin');
        }
    }

    async function handleMagicLink(e) {
        e.preventDefault();
        const email = $('auth-magic-email').value.trim();
        if (!email) { setError('Please enter your email address.'); return; }

        setLoading(true);
        setError('');

        const { error } = await window.SupabaseClient.auth.signInWithOtp({
            email,
            options: { emailRedirectTo: window.location.origin }
        });

        setLoading(false);

        if (error) { setError(mapAuthError(error)); return; }

        const pane = $('auth-pane-magic');
        if (pane) pane.innerHTML = `
            <div class="auth-success-msg">
                <div class="auth-success-icon">✉</div>
                <div class="auth-success-title">Check your email</div>
                <div class="auth-success-body">We sent a sign-in link to <strong>${email}</strong>. Click it to continue.</div>
            </div>`;
    }

    async function handleGoogle() {
        setError('');
        const { error } = await window.SupabaseClient.auth.signInWithOAuth({
            provider: 'google',
            options: { redirectTo: window.location.origin }
        });
        if (error) setError(mapAuthError(error));
    }

    function showConfirmationMessage(email) {
        const pane = $('auth-pane-email');
        if (pane) pane.innerHTML = `
            <div class="auth-success-msg">
                <div class="auth-success-icon">✉</div>
                <div class="auth-success-title">Confirm your email</div>
                <div class="auth-success-body">We sent a confirmation link to <strong>${email}</strong>. Click it, then sign in.</div>
                <button class="auth-link-btn" id="auth-back-signin">Back to sign in</button>
            </div>`;
        const back = $('auth-back-signin');
        if (back) back.addEventListener('click', () => { switchTab('email'); switchEmailMode('signin'); });
    }

    // ── Session & Storage Isolation ───────────────────────────────────────────

    function _purgeDatabase(dbName) {
        return new Promise((resolve) => {
            if (!('indexedDB' in window)) return resolve(true);
            let settled = false;
            const finish = (ok) => {
                if (settled) return;
                settled = true;
                resolve(ok);
            };
            try {
                const req = indexedDB.deleteDatabase(dbName);
                req.onsuccess = () => finish(true);
                req.onerror = () => finish(false);
                // A still-open connection blocks delete. Wait for it to close —
                // onsuccess fires after. Finishing false here leaks PDFs onto
                // the next sign-in once last_authenticated_user is cleared.
                req.onblocked = () => {};
                setTimeout(() => finish(false), 4000);
            } catch (_) {
                finish(false);
            }
        });
    }

    async function purgeAllLocalDatabases() {
        const results = [];
        if (window.PDFLibrary?.purgeDatabase) {
            try { results.push(await window.PDFLibrary.purgeDatabase()); }
            catch (_) { results.push(false); }
        } else {
            results.push(await _purgeDatabase('studymeneer_pdf_library'));
            try { localStorage.removeItem('pdf_library_meta'); } catch (_) {}
        }
        results.push(await _purgeDatabase('studymeneer_lecture_recordings'));
        try { localStorage.removeItem('lecture_session'); } catch (_) {}
        return results.every(Boolean);
    }

    async function onSessionReady(user) {
        hideAuthScreen();

        // Purge another student's device-only files. Missing last is a first
        // run after this ships (keep their library). pending_idb_purge means
        // the previous sign-out could not finish the IDB delete.
        const lastUser = localStorage.getItem('last_authenticated_user');
        const pendingPurge = localStorage.getItem(PENDING_IDB_PURGE_KEY) === '1';
        if ((lastUser && lastUser !== user.id) || pendingPurge) {
            await purgeAllLocalDatabases();
            try { localStorage.removeItem(PENDING_IDB_PURGE_KEY); } catch (_) {}
        }
        localStorage.setItem('last_authenticated_user', user.id);

        populateAccountUI(user);
        // Product insight is deliberately best-effort. It owns its IndexedDB
        // outbox and catches its own failures, so auth and studying never wait.
        void window.Analytics?.init();
        if (typeof window.initializeApp === 'function' && !_initialized) {
            _initialized = true;
            // Pull cloud data first, then boot the app
            const userId = user.id;
            if (window.CloudSync) {
                window.CloudSync.loadAllFromCloud(userId).finally(() => window.initializeApp());
            } else {
                window.initializeApp();
            }
        }
    }

    // ── Account UI population ─────────────────────────────────────────────────

    function populateAccountUI(user) {
        if (!user) return;
        const email    = user.email || '';
        const initials = _getInitials(email, user.user_metadata?.full_name);

        // Navbar avatar
        const navAvatar = $('navAvatar');
        if (navAvatar) navAvatar.textContent = initials;

        // Settings > Account pane (lazy partial may not exist at initial boot, so loadSettings re-runs this)
        const settingsAvatar = document.querySelector('#spane-account .settings-avatar');
        const settingsName   = document.querySelector('#spane-account .settings-account-name');
        const settingsEmail  = document.querySelector('#spane-account .settings-account-email');
        if (settingsAvatar) settingsAvatar.textContent = initials;
        if (settingsName)   settingsName.textContent   = user.user_metadata?.full_name || email.split('@')[0];
        if (settingsEmail)  settingsEmail.textContent  = email;

        // Hide Change Password when this account has no email/password identity
        // (Google-only). Email+Google users still have a password to change.

        const nameInput = $('accountDisplayNameInput');
        if (nameInput && !nameInput.value) {
            nameInput.value = user.user_metadata?.full_name || '';
        }

        const identities = user.identities;
        const hasPasswordAuth = Array.isArray(identities) && identities.length
            ? identities.some(id => id.provider === 'email')
            : user.app_metadata?.provider !== 'google';
        const changePwdGroup = $('accountChangePasswordGroup');
        if (changePwdGroup) {
            changePwdGroup.style.display = hasPasswordAuth ? 'block' : 'none';
        }

        // Logout button
        const logoutBtn = $('account-logout-btn');
        if (logoutBtn) logoutBtn.style.display = 'inline-flex';
    }

    function _getInitials(email, fullName) {
        if (fullName) {
            const parts = fullName.trim().split(/\s+/);
            return (parts[0][0] + (parts[1] ? parts[1][0] : '')).toUpperCase();
        }
        return email.substring(0, 2).toUpperCase();
    }

    // ── Sign out ──────────────────────────────────────────────────────────────

    async function signOut() {
        const isDirty = window.CloudSync?.isAnyDirty ? window.CloudSync.isAnyDirty() : false;
        let confirmMsg = 'Sign out of this device?\n\n' +
            'Uploaded textbook PDFs and recorded lecture audio live only on this device. They will be deleted on sign-out and cannot be recovered from the cloud.';

        if (isDirty) {
            confirmMsg = 'Some of your notes, tasks, or other work has not finished reaching the cloud. Signing out on this device now may mean that work is not on your other devices yet.\n\n' + confirmMsg;
        }

        if (!confirm(confirmMsg)) return;

        if (isDirty && window.CloudSync?.flush) {
            try { await window.CloudSync.flush(); } catch (_) {}
        }

        try {
            await window.SupabaseClient.auth.signOut();
        } catch (err) {
            console.warn('Sign out request error, clearing local storage anyway:', err);
        }

        const purged = await purgeAllLocalDatabases();
        localStorage.clear();
        if (!purged) {
            try { localStorage.setItem(PENDING_IDB_PURGE_KEY, '1'); } catch (_) {}
        }
        _initialized = false;
        showAuthScreen();
        setTimeout(() => window.location.reload(), 200);
    }

    // ── Init ──────────────────────────────────────────────────────────────────

    function bindUI() {
        // Tab buttons
        $('auth-tab-email')  && $('auth-tab-email').addEventListener('click',  () => switchTab('email'));
        $('auth-tab-magic')  && $('auth-tab-magic').addEventListener('click',  () => switchTab('magic'));
        $('auth-tab-google') && $('auth-tab-google').addEventListener('click', () => switchTab('google'));

        // Email form
        const emailForm = $('auth-email-form');
        if (emailForm) emailForm.addEventListener('submit', handleEmailSubmit);

        // Forgot password triggers
        const forgotLink = $('auth-forgot-link');
        if (forgotLink) forgotLink.addEventListener('click', (e) => { e.preventDefault(); switchPane('forgot'); });

        const backFromForgot = $('auth-back-from-forgot');
        if (backFromForgot) backFromForgot.addEventListener('click', (e) => { e.preventDefault(); switchTab('email'); });

        const forgotForm = $('auth-forgot-form');
        if (forgotForm) forgotForm.addEventListener('submit', handleForgotPasswordSubmit);

        // Reset password form
        const resetForm = $('auth-reset-form');
        if (resetForm) resetForm.addEventListener('submit', handleResetPasswordSubmit);

        // Email mode toggle link (initial render)
        const switchLink = $('auth-switch-link');
        if (switchLink) switchLink.addEventListener('click', (e) => { e.preventDefault(); switchEmailMode(_emailMode === 'signin' ? 'signup' : 'signin'); });

        // Magic link form
        const magicForm = $('auth-magic-form');
        if (magicForm) magicForm.addEventListener('submit', handleMagicLink);

        // Google button
        const googleBtn = $('auth-google-btn');
        if (googleBtn) googleBtn.addEventListener('click', handleGoogle);

        // Password visibility toggles
        const setupPwdToggle = (btnId, inputId) => {
            const btn = $(btnId);
            const input = $(inputId);
            if (!btn || !input) return;
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                const isPwd = input.type === 'password';
                input.type = isPwd ? 'text' : 'password';

                // setButtonChrome, NOT textContent. Writing textContent deletes
                // the hydrated <svg> the markup's data-icon produced, so the
                // control would carry a Lucide eye until first use and be blank
                // for the rest of the session — a defect that appears only after
                // an interaction, which no check of the initial render can see.
                //
                // eye-off means "click to hide", matching the state it moves TO,
                // which is the convention every password field in the wild uses.
                const name = isPwd ? 'eye-off' : 'eye';
                if (window.Icons?.setButtonChrome) window.Icons.setButtonChrome(btn, name, '', { size: 14 });
                else btn.textContent = isPwd ? '🔒' : '👁';
            });
        };

        setupPwdToggle('auth-toggle-password', 'auth-password-input');
        setupPwdToggle('auth-toggle-confirm-password', 'auth-password-confirm');
        setupPwdToggle('auth-toggle-reset-password', 'auth-reset-password');
        setupPwdToggle('auth-toggle-reset-confirm', 'auth-reset-confirm');

        // Legal overlay links
        const openTerms = async (e) => {
            e.preventDefault();
            await window.Overlays?.ensure('legal');
            const m = $('legalTermsModal');
            if (m) m.style.display = 'flex';
        };

        const openPrivacy = async (e) => {
            e.preventDefault();
            await window.Overlays?.ensure('legal');
            const m = $('legalPrivacyModal');
            if (m) m.style.display = 'flex';
        };

        // Closing: the overlay-action map in app.js is bound by initializeApp(),
        // which never runs for a signed-out visitor, so these popups opened and
        // could not be dismissed (QA AUTH-1). X and Close carry data-action;
        // the backdrop is the .legal-modal element itself.
        const closeLegal = () => {
            ['legalTermsModal', 'legalPrivacyModal'].forEach(id => {
                const m = $(id);
                if (m) m.style.display = 'none';
            });
        };
        document.addEventListener('click', (e) => {
            const t = e.target;
            if (t.closest?.('[data-action="legal-close"]') || t.classList?.contains('legal-modal')) closeLegal();
        });
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') closeLegal();
        });

        $('auth-terms-link')?.addEventListener('click', openTerms);
        $('auth-privacy-link')?.addEventListener('click', openPrivacy);
        $('auth-footer-terms')?.addEventListener('click', openTerms);
        $('auth-footer-privacy')?.addEventListener('click', openPrivacy);
    }

    function initAuth() {
        if (!window.SupabaseClient) {
            console.error('Auth: SupabaseClient not initialised.');
            return;
        }

        bindUI();

        // SIGNED_IN / INITIAL_SESSION fire before PASSWORD_RECOVERY on a reset
        // link. Defer boot so recovery can cancel it. A parallel session probe
        // here races the same way and was the premature CloudSync pull.
        window.SupabaseClient.auth.onAuthStateChange((event, session) => {
            if (event === 'PASSWORD_RECOVERY') {
                _isRecovery = true;
                if (_bootTimer) { clearTimeout(_bootTimer); _bootTimer = null; }
                showAuthScreen();
                switchPane('reset');
                return;
            }

            if (event === 'USER_UPDATED') {
                return;
            }

            if (session && session.user) {
                if (_isRecovery) return;
                const user = session.user;
                if (_bootTimer) clearTimeout(_bootTimer);
                _bootTimer = setTimeout(() => {
                    _bootTimer = null;
                    if (!_isRecovery) onSessionReady(user);
                }, 50);
            } else if (!_isRecovery) {
                _initialized = false;
                showAuthScreen();
            }
        });
    }

    // ── Public API ────────────────────────────────────────────────────────────
    window.Auth = {
        initAuth,
        showAuthScreen,
        hideAuthScreen,
        signOut,
        mapAuthError,
        purgeAllLocalDatabases,
        populateAccountUI
    };
    _exported = window.Auth;

})();

// ── ES module surface (roadmap §3) ────────────────────────────────────────────
export const Auth = _exported;
export default _exported;
